import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { Box, IconButton, Typography } from "@mui/material";
import { useUpdateBalanceVisibilityMutation } from "Api/Mutations/User/useUpdateBalanceVisibilityMutation";
import { useBankingAccounts } from "Api/Queries/Accounts/useBankingAccounts";
import { useUserInfoQuery } from "Auth/Api/Queries/useUserInfoQuery";
import type {
  BalanceHeaderProps,
  BalancesProps,
  CurrencyBalance,
  PaymentActionsProps,
} from "Components/Dashboard/BalanceWithPaymentActions/types";
import { useDashboardBalanceCurrency } from "Hooks/Dashboard/useDashboardBalanceCurrency";
import { useHasPrimaryAccount } from "Hooks/useHasPrimaryAccount";
import { useIsUserInMooneLite } from "Hooks/useIsUserInMooneLite";
import { useIsUserVerified } from "Hooks/useIsUserVerified";
import { useLoggedInUser } from "Hooks/useLoggedInUser";
import { BankAccountStatus } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Translations/Resources";
import { hasPreApprovedAccount, pickMainBankAccount } from "Utils/AccountUtils";
import { getRegionCurrency } from "Utils/CurrencyUtils";
import { appUrl } from "Utils/UrlUtils";
import type { FC } from "react";

const BalanceResources = Resources.Balance.BalanceContent;

type BalanceContainerProps = {
  /** Renders the selected currency's balance and applies the blur. */
  renderBalances: FC<BalancesProps>;
  /** Optional row above everything (title, eye toggle, currency switcher); see BalanceHeaderProps. */
  renderHeader?: FC<BalanceHeaderProps>;
  renderActions: FC<PaymentActionsProps>;
  renderLoading: FC;
  renderError: FC;
  colorVariant?: "light" | "dark";
};

export const BalanceContainer = ({
  renderBalances: Balances,
  renderHeader: Header,
  renderActions: Actions,
  renderLoading: Loading,
  renderError: ErrorComponent,
  colorVariant = "dark",
}: BalanceContainerProps) => {
  const { t } = useResource();
  const {
    data: { items: accounts } = {},
    isLoading: isLoadingBankAccounts,
    isError,
  } = useBankingAccounts();
  const { isUserVerified, isLoading: isLoadingUserVerification } =
    useIsUserVerified();
  const { isUserInBlockedMoonLite } = useIsUserInMooneLite();
  const { hasPrimaryAccount } = useHasPrimaryAccount();
  const { data: userInfo } = useUserInfoQuery();
  const currentUser = useLoggedInUser();
  const { mutate: updateBalanceVisibility, isPending: isUpdatingVisibility } =
    useUpdateBalanceVisibilityMutation();

  // Derived from whatever has loaded so far (empty while loading) — the currency selection hook
  // below must run on every render, so nothing may return before it.
  const activeAccounts = (accounts ?? []).filter(
    ({ status }) => status === BankAccountStatus.Active,
  );

  // Balances are summed per currency and rendered one row each — CZK and EUR
  // must never be added together. No active accounts renders a zero in the
  // user's market currency.
  const balancesByCurrency = activeAccounts.reduce<Map<string, number>>(
    (map, { balance, currency }) => {
      if (currency) {
        map.set(currency, (map.get(currency) ?? 0) + (balance ?? 0));
      }
      return map;
    },
    new Map(),
  );

  const marketCurrency = currentUser?.region
    ? getRegionCurrency(currentUser.region)
    : undefined;

  const displayBalances: ReadonlyArray<CurrencyBalance> =
    balancesByCurrency.size > 0
      ? [...balancesByCurrency.entries()]
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([currency, total]) => ({ currency, total }))
      : [{ currency: marketCurrency, total: 0 }];

  const currencies = displayBalances.flatMap(({ currency }) =>
    currency ? [currency] : [],
  );
  const { selectedCurrency, selectCurrency } = useDashboardBalanceCurrency(
    currencies,
    marketCurrency,
  );

  const isLoading = isLoadingBankAccounts || isLoadingUserVerification;
  const isReady = !isLoading && !isError && accounts != null;

  // Dashboard CTAs mint payments in the market currency (useAmount) — default them
  // to the market rail's main account.
  const primaryAccountID = pickMainBankAccount(
    accounts ?? [],
    marketCurrency,
  )?.publicID;
  const sendPaymentLink = primaryAccountID
    ? `${appUrl("send-payment")}?publicID=${encodeURIComponent(primaryAccountID)}`
    : appUrl("send-payment");
  const payMeLink = primaryAccountID
    ? `${appUrl("initiate-form-payment")}?publicID=${encodeURIComponent(primaryAccountID)}`
    : appUrl("initiate-form-payment");

  const isPreApproved = hasPreApprovedAccount(accounts ?? []);
  const isEverythingDisabled = !primaryAccountID || isUserInBlockedMoonLite;
  const isPayMeDisabled =
    isEverythingDisabled ||
    (!isUserVerified && !isPreApproved) ||
    !hasPrimaryAccount;
  const isSendPaymentDisabled =
    isEverythingDisabled ||
    (accounts ?? []).every(({ isReceiveOnly }) => isReceiveOnly);

  const isBalanceVisible =
    !isUserVerified || (userInfo?.isBalanceVisible ?? false);
  const isBalanceBlurred = !isBalanceVisible;

  // Handed over as a node rather than laid out here. Exactly one slot receives it: the header
  // when the page registers one (desktop), otherwise the balances renderer, which places it
  // next to the amount (mobile).
  const visibilityToggle = (
    <IconButton
      size="small"
      aria-label={
        isBalanceVisible
          ? t(BalanceResources.HideBalance)
          : t(BalanceResources.ShowBalance)
      }
      disabled={isUpdatingVisibility || !userInfo}
      onClick={() =>
        userInfo &&
        updateBalanceVisibility({
          isVisible: !userInfo.isBalanceVisible,
        })
      }
      sx={{
        borderRadius: 1.5,
        ...(colorVariant === "light"
          ? {
              color: "primary.contrastText",
              "&.Mui-disabled": {
                color: "primary.contrastText",
              },
            }
          : {
              color: "primary.main",
              "&.Mui-disabled": {
                color: "primary.main",
              },
            }),
      }}
    >
      {isBalanceVisible ? (
        <VisibilityOutlinedIcon fontSize="small" />
      ) : (
        <VisibilityOffOutlinedIcon fontSize="small" />
      )}
    </IconButton>
  );

  const canSwitchCurrency = isReady && isUserVerified;
  const header = Header ? (
    <Header
      visibilityToggle={canSwitchCurrency ? visibilityToggle : null}
      currencies={canSwitchCurrency ? currencies : []}
      selectedCurrency={canSwitchCurrency ? selectedCurrency : undefined}
      selectCurrency={selectCurrency}
    />
  ) : null;

  if (isLoading) {
    return (
      <>
        {header}
        <Loading />
      </>
    );
  }
  if (!isReady) {
    return (
      <>
        {header}
        <ErrorComponent />
      </>
    );
  }

  return (
    <>
      {header}
      {isUserVerified ? (
        <Balances
          balances={displayBalances}
          currencies={currencies}
          selectedCurrency={selectedCurrency}
          selectCurrency={selectCurrency}
          isBalanceBlurred={isBalanceBlurred}
          visibilityToggle={Header ? null : visibilityToggle}
        />
      ) : (
        <Box mt={1.5}>
          <Typography color="text.secondary" fontWeight={400}>
            {t(BalanceResources.HiddenBalance)}
          </Typography>
        </Box>
      )}
      <Actions
        payMeAction={{
          text: t(BalanceResources.PayMeAction),
          link: payMeLink,
          isDisabled: isPayMeDisabled,
        }}
        sendPaymentAction={{
          text: t(BalanceResources.SendPaymentAction),
          link: sendPaymentLink,
          isDisabled: isSendPaymentDisabled,
        }}
      />
    </>
  );
};
